import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackEvent, captureUTM } from "@/lib/analytics";
import {
  Building2, Users, Globe, ArrowRight, CheckCircle2, Briefcase,
  Search, FileText, Shield, Clock, Zap
} from "lucide-react";

const STEPS = [
  { icon: Building2, titleKey: "fh.step1_title", descKey: "fh.step1_desc" },
  { icon: FileText, titleKey: "fh.step2_title", descKey: "fh.step2_desc" },
  { icon: Users, titleKey: "fh.step3_title", descKey: "fh.step3_desc" },
];

const BENEFITS = [
  { icon: Globe, titleKey: "fh.ben1_title", descKey: "fh.ben1_desc" },
  { icon: Search, titleKey: "fh.ben2_title", descKey: "fh.ben2_desc" },
  { icon: Zap, titleKey: "fh.ben3_title", descKey: "fh.ben3_desc" },
  { icon: Shield, titleKey: "fh.ben4_title", descKey: "fh.ben4_desc" },
  { icon: Clock, titleKey: "fh.ben5_title", descKey: "fh.ben5_desc" },
  { icon: Briefcase, titleKey: "fh.ben6_title", descKey: "fh.ben6_desc" },
];

export default function ForHotelsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // CRITICAL ORDER: capture UTM FIRST, then track with it
    const utm = captureUTM(searchParams);
    trackEvent("hotel_landing_view", {}, Object.keys(utm).length > 0 ? utm : null);
  }, [searchParams]);

  const handleCTA = () => {
    trackEvent("hotel_signup_started");
    navigate("/auth?mode=register&role=hotel");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="for-hotels-page">
      <Helmet>
        <title>{t("fh.seo_title")}</title>
        <meta name="description" content={t("fh.seo_desc")} />
        <link rel="canonical" href="https://staffera.eu/for-hotels" />
        <meta property="og:title" content={t("fh.seo_title")} />
        <meta property="og:description" content={t("fh.seo_desc")} />
        <meta property="og:url" content="https://staffera.eu/for-hotels" />
      </Helmet>

      {/* Navbar */}
      <header className="bg-navy-900 border-b border-navy-800">
        <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div onClick={() => navigate("/")} className="cursor-pointer flex-shrink-0"><Logo size="md" className="text-white" /></div>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="dark" />
            <Button variant="ghost" className="text-slate-300 hover:text-white text-sm" onClick={() => navigate("/auth?role=hotel")}>{t("nav.login")}</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-sm" onClick={handleCTA}>
              {t("fh.cta_short")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-navy-900 pt-16 pb-20 px-4" data-testid="fh-hero">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-sans font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4 leading-tight">
            {t("fh.hero_title")}
          </h1>
          <p className="text-base sm:text-lg text-slate-400 font-body max-w-2xl mx-auto mb-8">
            {t("fh.hero_desc")}
          </p>
          <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm px-10 h-13 text-base shadow-lg" onClick={handleCTA} data-testid="fh-hero-cta">
            <Briefcase className="w-5 h-5 mr-2" /> {t("fh.cta_main")} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <p className="text-sm text-slate-500 font-body mt-4">{t("fh.hero_sub")}</p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-sans font-bold text-2xl text-center text-slate-900 mb-12">{t("fh.how_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-xs text-emerald-600 font-semibold font-body mb-1">{t("fh.step_label")} {i + 1}</p>
                <h3 className="font-sans font-semibold text-base text-slate-900 mb-2">{t(step.titleKey)}</h3>
                <p className="text-sm text-slate-500 font-body">{t(step.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-sans font-bold text-2xl text-center text-slate-900 mb-12">{t("fh.benefits_title")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((ben, i) => (
              <Card key={i} className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-5">
                  <ben.icon className="w-8 h-8 text-emerald-500 mb-3" />
                  <h3 className="font-sans font-semibold text-sm text-slate-900 mb-1">{t(ben.titleKey)}</h3>
                  <p className="text-sm text-slate-500 font-body">{t(ben.descKey)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who is Staffera for */}
      <section className="py-16 md:py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-sans font-bold text-2xl text-slate-900 mb-4">{t("fh.who_title")}</h2>
          <p className="text-base text-slate-500 font-body mb-8">{t("fh.who_desc")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {["fh.who1", "fh.who2", "fh.who3", "fh.who4"].map(k => (
              <span key={k} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-body text-slate-700">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {t(k)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 px-4 bg-navy-900">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-sans font-bold text-2xl text-white mb-4">{t("fh.final_title")}</h2>
          <p className="text-base text-slate-400 font-body mb-8">{t("fh.final_desc")}</p>
          <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm px-10 h-12 text-base" onClick={handleCTA} data-testid="fh-final-cta">
            {t("fh.cta_main")} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
